import { parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js/max";

/**
 * Same approach as the b2c app's phone-validation-utils.ts, adapted for this
 * app's contact-phone fields (Host, Supplier) rather than an OTP signup flow —
 * any valid phone type is accepted here (a supplier's business line can be a
 * landline), not just mobile/fixed-line-or-mobile.
 *
 * The real API requires E.164 (country dial code included, e.g. "+390559901234")
 * for both UpdateHostDto.phone and UpdateSupplierDto.contactPhone — confirmed
 * against the live Swagger schema. This only checks *format* client-side; the
 * API remains the source of truth and is never bypassed.
 */
export interface PhoneValidationResult {
  isValid: boolean;
  error?: string;
}

export const INVALID_PHONE_MESSAGE = "Please enter a valid phone number, including the country code.";

/**
 * Validates a phone number against E.164 rules for the given country.
 * Empty input is treated as valid — required-ness is a separate concern.
 */
export function validatePhoneNumber(phone: string, countryCode: CountryCode): PhoneValidationResult {
  if (!phone || !phone.trim()) return { isValid: true };

  try {
    const phoneNumber = parsePhoneNumberFromString(phone.trim(), countryCode);
    if (!phoneNumber || !phoneNumber.isValid()) {
      return { isValid: false, error: INVALID_PHONE_MESSAGE };
    }
    return { isValid: true };
  } catch {
    return { isValid: false, error: INVALID_PHONE_MESSAGE };
  }
}

/** Formats a valid phone number as E.164 (e.g. "+390559901234") for the API. Returns the raw input if it doesn't parse. */
export function toE164(phone: string, countryCode: CountryCode): string {
  const parsed = parsePhoneNumberFromString(phone.trim(), countryCode);
  return parsed?.isValid() ? parsed.number : phone.trim();
}
